<script setup lang="ts">
  import { ref } from 'vue';
  import TotpVerification from '@/common/shared/TotpVerification.vue';
  import RestoreAccessInfoDialog from '@/common/components/user/RestoreAccessInfoDialog.vue';

  interface TotpObject {
    code: string;
  }

  interface ErrorsObject {
    backend?: string;
    [key: string]: any;
  }

  interface Props {
    totp: TotpObject;
    rules?: Record<string, any>;
    errors: ErrorsObject;
    pending: boolean;
    username?: string;
  }

  type EmitEvents = {
    (event: 'submit'): void;
    (event: 'clearErrors'): void;
    (event: 'back'): void;
  }

  const props = defineProps<Props>();
  const emit = defineEmits<EmitEvents>();

  const verificationRef = ref<InstanceType<typeof TotpVerification>>();
  const restoreAccessInfoDialogRef = ref<InstanceType<typeof RestoreAccessInfoDialog>>();

  const handleSubmit = (code: string) => {
    props.totp.code = code;
    emit('submit');
  };

  const clearErrors = () => {
    verificationRef.value?.clearErrors();
    emit('clearErrors');
  };

  const onBack = () => {
    emit('back');
  };

  const handleHelpRequested = () => {
    restoreAccessInfoDialogRef.value?.create();
  };

  const validate = (callback?: (valid: boolean) => void) => {
    if (verificationRef.value) {
      const { formRef } = verificationRef.value;
      if (formRef) {
        return formRef.validate(callback);
      }
    }
    if (callback) {
      callback(false);
    }

    return false;
  };

  defineExpose({
    validate,
    clearErrors
  });
</script>

<template>
<div data-component-name="totp-login-form" class="login-form">
  <TotpVerification data-component-name="totp-verification"
    ref="verificationRef"
    :pending="pending"
    :error="errors.backend"
    :username="username"
    @submit="handleSubmit"
    @cancel="onBack"
    @helpRequested="handleHelpRequested"
  />
  <RestoreAccessInfoDialog data-component-name="restore-access-info-dialog" ref="restoreAccessInfoDialogRef" />
</div>
</template>

<style lang="scss" scoped>
  .login-form {
    width: 512px;
    padding: 24px;
    margin: 0 auto;
    border-radius: 5px;
    background: $av-inversed-primary;
    position: relative;
  }
</style>
